import { EAPExperienceStatus } from '@headway/api/models/EAPExperienceStatus';
import { UserRead } from '@headway/api/models/UserRead';
import {
  NavigationLink as HelixNavigationLink,
  NavigationDivider,
  NavigationMenu,
  NavigationMenuButton,
  NavigationMenuItem,
  NavigationSubMenuContext,
  NotificationDot,
} from '@headway/helix/alpha/Navigation';
import { ContentText } from '@headway/helix/ContentText';
import { Divider } from '@headway/helix/Divider';
import type { LinkProps } from '@headway/helix/Link';
import { useAppointmentReadiness } from '@headway/shared/hooks/useAppointmentReadiness';
import { useUserEAPExperience } from '@headway/shared/hooks/useUserEAPExperience';
import {
  formatPatientName,
  shouldDisplayAccountNotificationBadge,
  shouldDisplayBenefitNotificationBadge,
} from '@headway/shared/utils/patient';
import { trackHelpCenterLinkClicked } from '@headway/ui/utils/supportAnalyticEvents';

import { useBillingEvents } from '../../contexts';
import { Link } from '../Link';

type AccountMenuContentProps = {
  user: any;
  impersonatingUser?: UserRead;
  logout: () => void;
  stopSpoofing: () => void;
};

const AccountMenuContent = ({
  user,
  impersonatingUser,
  logout,
  stopSpoofing,
}: AccountMenuContentProps) => {
  const { data: appointmentReadiness } = useAppointmentReadiness(user.id);

  const { data: userEAPExperience, isLoading: isLoadingIsUserEAPExperience } =
    useUserEAPExperience(user.id);

  const isUserEAPExperienceActive =
    userEAPExperience?.status === EAPExperienceStatus.ACTIVE;

  const {
    partitionedBillingEvents: { needsAttention: billingEventsAwaitingAction },
  } = useBillingEvents();

  const showBillingNotificationBadge =
    !!billingEventsAwaitingAction.length ||
    appointmentReadiness?.paymentMethod?.length;

  const showAccountNotificationBadge =
    appointmentReadiness &&
    shouldDisplayAccountNotificationBadge(appointmentReadiness);

  const showBenefitsNotificationBadge =
    appointmentReadiness &&
    shouldDisplayBenefitNotificationBadge({
      appointmentReadiness,
    });

  const isImpersonator =
    !!impersonatingUser && impersonatingUser.id !== user.id;
  return (
    <>
      <div className="flex flex-col gap-2 p-4">
        {formatPatientName(user) ? (
          <ContentText variant="body-small/medium">
            {formatPatientName(user)}
          </ContentText>
        ) : null}
        <ContentText variant="body-small">{user.email}</ContentText>
      </div>
      <Divider autoSpacing={false} />
      <div className="hlx-navigation" data-hlx-alpha={true}>
        <NavigationSubMenuContext.Provider
          value={{ id: 'account-menu', title: 'Account Menu' }}
        >
          <NavigationMenu>
            <AgoraNavigationLink href="/account">
              Account
              <NotificationDot
                hidden={!showAccountNotificationBadge}
                variant="negative"
                aria-label="Needs attention"
              />
            </AgoraNavigationLink>
            <AgoraNavigationLink href="/benefits">
              Benefits
              <NotificationDot
                hidden={!showBenefitsNotificationBadge}
                variant="negative"
                aria-label="Needs attention"
              />
            </AgoraNavigationLink>
            <AgoraNavigationLink href="/appointments/upcoming">
              Appointments
            </AgoraNavigationLink>
            {!isUserEAPExperienceActive && !isLoadingIsUserEAPExperience && (
              <AgoraNavigationLink href="/billing">
                Billing
                <NotificationDot
                  hidden={!showBillingNotificationBadge}
                  variant="negative"
                  aria-label="Needs attention"
                />
              </AgoraNavigationLink>
            )}
            <AgoraNavigationLink href="/messages">Messages</AgoraNavigationLink>
            <AgoraNavigationLink href="/settings">Settings</AgoraNavigationLink>
            <AgoraNavigationLink href="/">Find a provider</AgoraNavigationLink>

            <NavigationDivider />
            <AgoraNavigationLink
              href="/support"
              target="_blank"
              rel="noreferrer"
              onClick={() =>
                trackHelpCenterLinkClicked(
                  'agora_sidebar_entrypoint',
                  user.id,
                  'Help Center'
                )
              }
            >
              Help Center
            </AgoraNavigationLink>
            <NavigationDivider />
            {isImpersonator && (
              <NavigationMenuItem>
                <NavigationMenuButton
                  onPress={() => {
                    stopSpoofing();
                  }}
                  icon={null}
                >
                  Stop Spoofing
                </NavigationMenuButton>
              </NavigationMenuItem>
            )}
            <NavigationMenuItem>
              <NavigationMenuButton
                onPress={() => {
                  logout();
                }}
                icon={null}
              >
                Log Out
              </NavigationMenuButton>
            </NavigationMenuItem>
          </NavigationMenu>
        </NavigationSubMenuContext.Provider>
      </div>
    </>
  );
};

type AgoraNavigationLinkProps = Omit<
  Parameters<typeof Link>[0],
  'onFocus' | 'onBlur' | 'onFocusCapture' | 'onBlurCapture'
>;

function AgoraNavigationLink(props: AgoraNavigationLinkProps) {
  return (
    <HelixNavigationLink
      icon={null}
      component={Link}
      {...(props as Omit<LinkProps<typeof Link>, 'icon' | 'component'>)}
    >
      {props.children}
    </HelixNavigationLink>
  );
}

export default AccountMenuContent;
